import { useState } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import digitalTransformationImage from '@/assets/images/digital-transformation.jpg';
import dataIntelligenceImage from '@/assets/images/data-inteligence.avif';
import appliedAiImage from '@/assets/images/applied-ai.avif';

const solutions = [
  {
    id: 'digital',
    icon: 'ri-rocket-line',
    badge: 'Transformação Digital',
    title: 'Da operação manual à operação inteligente',
    desc: 'Mapeamos todos os processos da sua empresa e desenhamos uma estratégia de modernização passo a passo. Substituímos planilhas, e-mails e tarefas manuais por sistemas integrados, automáticos e rastreáveis.',
    benefits: [
      'Redução de até 70% em tarefas repetitivas',
      'Rastreabilidade total de processos',
      'Integração entre áreas e sistemas',
      'Tomada de decisão baseada em dados reais',
    ],
    image: digitalTransformationImage,
  },
  {
    id: 'data',
    icon: 'ri-pie-chart-2-line',
    badge: 'Data Intelligence',
    title: 'Transforme dados em decisões estratégicas',
    desc: 'Integramos dados de múltiplas fontes — ERP, CRM, planilhas, sensores — e criamos dashboards e modelos analíticos que revelam padrões, oportunidades e riscos invisíveis para o olho humano.',
    benefits: [
      'Visão 360º do negócio em tempo real',
      'Previsão de demanda e churn',
      'Segmentação de clientes e produtos',
      'KPIs estratégicos automatizados',
    ],
    image: dataIntelligenceImage,
  },
  {
    id: 'ai',
    icon: 'ri-ai-generate',
    badge: 'IA Aplicada',
    title: 'Inteligência Artificial no coração do seu negócio',
    desc: 'Implementamos modelos de IA diretamente nos fluxos operacionais da sua empresa — desde assistentes virtuais com LLMs até sistemas de recomendação, detecção de anomalias e automação cognitiva.',
    benefits: [
      'Assistentes virtuais com contexto do negócio',
      'Automação cognitiva de processos complexos',
      'Detecção de fraudes e anomalias',
      'Recomendações inteligentes de produtos/serviços',
    ],
    image: appliedAiImage,
  },
];

export default function SolutionsSection() {
  const { ref, visible } = useScrollAnimation();
  const [active, setActive] = useState(0);

  const current = solutions[active];

  return (
    <section id="solutions" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div ref={ref} className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="inline-block text-emerald-600 font-semibold text-sm font-inter tracking-widest uppercase mb-3">Como atuamos</span>
          <h2 className="font-sora font-bold text-3xl md:text-4xl text-zinc-900 mb-4">Soluções que Transformam</h2>
          <p className="font-inter text-zinc-500 max-w-xl mx-auto text-base">
            Atuamos em três grandes frentes estratégicas que cobrem as principais necessidades de transformação digital das empresas.
          </p>
          <div className="w-12 h-1 bg-emerald-500 rounded-full mx-auto mt-5" />
        </div>

        {/* Tab selector */}
        <div className={`flex flex-wrap justify-center gap-3 mb-12 transition-all duration-700 delay-150 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {solutions.map((s, i) => (
            <button
              key={s.id}
              onClick={() => setActive(i)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold font-sora transition-all cursor-pointer whitespace-nowrap ${
                active === i
                  ? 'bg-emerald-500 text-white'
                  : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
              }`}
            >
              <i className={`${s.icon} text-base`} />
              {s.badge}
            </button>
          ))}
        </div>

        {/* Solution detail */}
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center transition-all duration-500 ${visible ? 'opacity-100' : 'opacity-0'}`}>
          {/* Text */}
          <div>
            <span className="inline-flex items-center gap-2 text-emerald-600 font-semibold text-sm font-inter tracking-widest uppercase mb-4">
              <i className={`${current.icon} text-base`} />
              {current.badge}
            </span>
            <h3 className="font-sora font-bold text-2xl md:text-3xl text-zinc-900 mb-5 leading-snug">
              {current.title}
            </h3>
            <p className="font-inter text-zinc-600 text-base leading-relaxed mb-7">{current.desc}</p>

            <ul className="space-y-3">
              {current.benefits.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-emerald-500 text-base font-bold" />
                  </div>
                  <span className="font-inter text-zinc-700 text-sm">{b}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="mt-8 bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-full text-sm font-semibold font-sora transition-all cursor-pointer whitespace-nowrap"
            >
              Quero essa Solução
            </button>
          </div>

          {/* Image */}
          <div className="rounded-2xl overflow-hidden w-full h-72 md:h-80">
            <img
              src={current.image}
              alt={current.title}
              className="w-full h-full object-cover object-top transition-all duration-500"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
